/** @jsxImportSource react */
'use client';

import { motion } from 'motion/react';
import type { ReactNode } from 'react';

import { ArrowRight, Check } from './icons';
import { Eyebrow, Heading, Section, Subheading } from './section';
import { SignupLink } from './signup-link';
import { TrainingLink } from './training-link';

// Shared data shape for any "OnArk vs X" comparison page.
// Each comparison renders the same structure with different inputs.

export interface ComparisonData {
  competitorName: string;
  competitorShort: string; // for shorter mentions
  hero: {
    headline: ReactNode;
    subhead: string;
    surveyStat: string; // e.g. "8 of 23 customers switched from this"
  };
  rows: {
    capability: string;
    competitor: { has: boolean; note?: string };
    onark: { has: boolean; note?: string };
  }[];
  switchStory: {
    quote: string;
    attribution: string;
    role: string;
  };
  bestFor: {
    competitor: string;
    onark: string;
  };
}

export function ComparisonPage({ data }: { data: ComparisonData }) {
  const source = `vs_${data.competitorShort
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')}`;

  return (
    <>
      {/* Hero */}
      <Section size="md">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-[1.3fr_1fr] lg:items-end">
          <div className="flex flex-col gap-7">
            <Eyebrow>Compare</Eyebrow>
            <Heading as="h1" size="xl">
              {data.hero.headline}
            </Heading>
            <Subheading className="max-w-[620px] text-[color:var(--color-gray-dark)]">
              {data.hero.subhead}
            </Subheading>
          </div>
          <div className="flex flex-col gap-3 self-stretch rounded-[20px] bg-[color:var(--color-bg-mist)] p-7">
            <span className="text-[12px] font-medium uppercase tracking-[0.14em] text-[color:var(--color-amethyst)]">
              From our 2026 survey
            </span>
            <p className="text-[18px] leading-[1.55] text-[color:var(--color-ink)]">
              {data.hero.surveyStat}
            </p>
          </div>
        </div>
      </Section>

      {/* Comparison table */}
      <Section size="md" tone="mist">
        <div className="flex flex-col gap-10">
          <div className="flex max-w-[680px] flex-col gap-5">
            <Eyebrow>Side by side</Eyebrow>
            <Heading size="lg">
              What you get with each{' '}
              <span className="italic [font-family:var(--font-serif)] font-light">
                option.
              </span>
            </Heading>
          </div>

          {/* Semantic ARIA table: the grid is visual, but role="table" + row/
              cell roles and the sr-only Yes/No in each cell mean AI crawlers,
              buying agents, and screen readers all read a real comparison —
              not a wall of unlabelled checkmark icons. */}
          <motion.div
            aria-label={`Feature comparison: OnArk versus ${data.competitorName}`}
            className="overflow-hidden rounded-[20px] bg-white ring-1 ring-[color:var(--color-border-soft)]"
            initial={{ opacity: 0, y: 16 }}
            role="table"
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ margin: '-100px', once: true }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            {/* Header row */}
            <div
              className="grid grid-cols-[1.4fr_1fr_1fr] items-center gap-3 border-b border-[color:var(--color-border-soft)] bg-[color:var(--color-bg-soft)] px-5 py-5 sm:px-8"
              role="row"
            >
              <span
                className="text-[12px] font-medium uppercase tracking-[0.14em] text-[color:var(--color-gray-med)]"
                role="columnheader"
              >
                What you need
              </span>
              <span
                className="text-center text-[15px] font-semibold tracking-[-0.005em] text-[color:var(--color-gray-dark)]"
                role="columnheader"
              >
                {data.competitorName}
              </span>
              <span
                className="text-center text-[15px] font-semibold tracking-[-0.005em] text-[color:var(--color-amethyst)]"
                role="columnheader"
              >
                OnArk
              </span>
            </div>

            {/* Rows */}
            {data.rows.map((row, i) => (
              <div
                key={row.capability}
                className={[
                  'grid grid-cols-[1.4fr_1fr_1fr] items-center gap-3 px-5 py-5 sm:px-8',
                  i < data.rows.length - 1
                    ? 'border-b border-[color:var(--color-border-soft)]'
                    : '',
                ].join(' ')}
                role="row"
              >
                <span
                  className="text-[15px] font-medium leading-[1.4] tracking-[-0.005em] text-[color:var(--color-ink)] sm:text-[16px]"
                  role="rowheader"
                >
                  {row.capability}
                </span>
                <Cell value={row.competitor} variant="competitor" />
                <Cell value={row.onark} variant="onark" />
              </div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* Switch story */}
      <Section size="md">
        <div className="mx-auto flex max-w-[920px] flex-col items-center gap-7 text-center">
          <svg
            className="opacity-30"
            fill="none"
            height="36"
            viewBox="0 0 32 32"
            width="36"
          >
            <path
              d="M8 22 Q8 14 14 11 L14 14 Q11 16 11 20 L13 20 L13 24 L8 24 Z M20 22 Q20 14 26 11 L26 14 Q23 16 23 20 L25 20 L25 24 L20 24 Z"
              fill="var(--color-amethyst)"
            />
          </svg>
          <p className="text-[clamp(22px,3vw,30px)] font-medium leading-[1.45] tracking-[-0.01em] text-[color:var(--color-ink)]">
            &ldquo;{data.switchStory.quote}&rdquo;
          </p>
          <div className="flex flex-col items-center gap-0.5 pt-2">
            <span className="text-[15px] font-medium text-[color:var(--color-ink)]">
              {data.switchStory.attribution}
            </span>
            <span className="text-[13px] text-[color:var(--color-gray-dark)]">
              {data.switchStory.role}
            </span>
          </div>
        </div>
      </Section>

      {/* Honest "best for" comparison */}
      <Section size="md" tone="mist">
        <div className="flex flex-col gap-10">
          <div className="flex max-w-[640px] flex-col gap-5">
            <Eyebrow>Honest take</Eyebrow>
            <Heading size="lg">
              When each one&apos;s{' '}
              <span className="italic [font-family:var(--font-serif)] font-light">
                actually
              </span>{' '}
              right.
            </Heading>
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
            <div className="flex flex-col gap-4 rounded-[20px] bg-white p-9 ring-1 ring-[color:var(--color-border-soft)]">
              <span className="text-[13px] font-medium uppercase tracking-[0.12em] text-[color:var(--color-gray-med)]">
                Stick with {data.competitorShort} if
              </span>
              <p className="text-[17px] leading-[1.6] text-[color:var(--color-gray-dark)]">
                {data.bestFor.competitor}
              </p>
            </div>
            <div className="flex flex-col gap-4 rounded-[20px] bg-[color:var(--color-ink)] p-9 text-white">
              <span className="text-[13px] font-medium uppercase tracking-[0.12em] text-[color:var(--color-highlight)]">
                Switch to OnArk if
              </span>
              <p className="text-[17px] leading-[1.6] text-[color:var(--color-gray-light)]">
                {data.bestFor.onark}
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section size="lg" tone="ink">
        <div className="flex flex-col items-center gap-8 text-center">
          <Eyebrow tone="highlight">Try it free</Eyebrow>
          <Heading as="h2" className="text-white" size="xl">
            See what changes in 30 minutes.
          </Heading>
          <Subheading className="max-w-[560px] text-[color:var(--color-gray-light)]">
            Free for 21 days, $0 today. Same playbook your sister churches used.
          </Subheading>
          <div className="flex flex-wrap items-center justify-center gap-5 pt-2">
            <SignupLink
              className="group inline-flex items-center gap-3 rounded-[14px] bg-[color:var(--color-highlight)] px-9 py-5 text-[17px] font-semibold text-[color:var(--color-ink)] transition-all hover:-translate-y-0.5"
              source={source}
            >
              <span>Try OnArk free</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </SignupLink>
            <TrainingLink
              className="link-underline text-[17px] font-semibold text-white transition-colors hover:text-[color:var(--color-highlight)]"
              source={source}
            >
              Or see how it works →
            </TrainingLink>
          </div>
        </div>
      </Section>
    </>
  );
}

function Cell({
  value,
  variant,
}: {
  value: { has: boolean; note?: string };
  variant: 'competitor' | 'onark';
}) {
  const positive = value.has;

  return (
    <div className="flex flex-col items-center gap-1.5 text-center" role="cell">
      {/* Text answer for AI extraction, agents, and screen readers — the icon
          alone carries no meaning to anything that isn't looking at pixels. */}
      <span className="sr-only">{positive ? 'Yes' : 'No'}</span>
      {positive ? (
        <Check
          aria-hidden
          className={[
            'h-5 w-5',
            variant === 'onark'
              ? 'text-[color:var(--color-amethyst)]'
              : 'text-[color:var(--color-gray-dark)]',
          ].join(' ')}
        />
      ) : (
        <span
          aria-hidden
          className="text-[18px] leading-none text-[color:var(--color-gray-light)]"
        >
          —
        </span>
      )}
      {value.note && (
        <span className="text-[12px] leading-[1.4] text-[color:var(--color-gray-med)]">
          {value.note}
        </span>
      )}
    </div>
  );
}
